import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import api from "../../services/api";

const MenuForm = ({ initialData = null, isEdit = false, onCancel = null, onSuccess = null }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    description: initialData?.description || "",
    price: initialData?.price || "",
    discountType: initialData?.discountType || "price",
    discountPercentage: initialData?.discountPercentage || "",
    discountPrice: initialData?.discountPrice || "",
    image: null,
    dietaryType: initialData?.dietaryType || "veg",
    brandType: initialData?.brandType || "gravy_ganj",
    category: initialData?.category || "",
    specialTag: initialData?.specialTag || "none",
    isAvailable: initialData?.isAvailable !== undefined ? initialData.isAvailable : true,
    subMenu: (initialData?.subMenu || []).map(item => ({
      ...item,
      discountType: item.discountType || "price",
      discountPercentage: item.discountPercentage || ""
    })),
    portions: (initialData?.portions || []).map(p => ({
      ...p,
      discountType: p.discountType || "price",
      discountPercentage: p.discountPercentage || ""
    })),
  });

  const [pricingType, setPricingType] = useState(
    initialData?.portions?.length > 0 ? "portions" : "single"
  );
  const fileInputRef = useRef(null);

  const categoryOptions = {
    kepsa_queue: [
      "Veg Biryani",
      "Non-veg biryani",
      "Veg kepsa",
      "Non-veg kepsa",
      "Veg starters",
      "Non-veg starters",
      "Beverages",
    ],
    gravy_ganj: [
      "Veg rice bowl",
      "Non-veg rice bowl",
      "Veg main course",
      "Non-veg main course",
      "Veg starters",
      "Non-veg starters",
      "Beverages",
    ],
    gutful_greens: [
      "Low-Kcal Protein Biryani",
      "Protein bowls",
      "Salad bowls",
      "Veggie super salad",
      "Flavoured protein",
      "Beverages",
    ],
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // Calculate discountPrice if type is percentage
      if (updated.discountType === "percentage" && (name === "price" || name === "discountPercentage" || name === "discountType")) {
        const price = parseFloat(updated.price);
        const percentage = parseFloat(updated.discountPercentage);
        if (!isNaN(price) && !isNaN(percentage)) {
          updated.discountPrice = Math.round(price - (price * percentage / 100)).toString();
        }
      }
      return updated;
    });
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, image: e.target.files[0] }));
  };

  // --- Portion Handlers ---
  const addPortion = () => {
    setFormData((prev) => ({
      ...prev,
      portions: [...prev.portions, { size: "small", price: "", discountPrice: "", discountType: "price", discountPercentage: "" }],
    }));
  };

  const removePortion = (index) => {
    setFormData((prev) => ({
      ...prev,
      portions: prev.portions.filter((_, i) => i !== index),
    }));
  };

  const handlePortionChange = (index, e) => {
    const { name, value } = e.target;
    const updatedPortions = [...formData.portions];
    updatedPortions[index][name] = value;

    const portion = updatedPortions[index];
    if (portion.discountType === "percentage" && (name === "price" || name === "discountPercentage" || name === "discountType")) {
      const p = parseFloat(portion.price);
      const pct = parseFloat(portion.discountPercentage);
      if (!isNaN(p) && !isNaN(pct)) {
        portion.discountPrice = Math.round(p - (p * pct / 100)).toString();
      }
    }

    setFormData((prev) => ({ ...prev, portions: updatedPortions }));
  };

  // --- SubMenu Handlers ---
  const addSubMenuItem = () => {
    setFormData((prev) => ({
      ...prev,
      subMenu: [
        ...prev.subMenu,
        {
          name: "",
          description: "",
          price: "",
          discountPrice: "",
          discountType: "price",
          discountPercentage: "",
          dietaryType: "veg",
        },
      ],
    }));
  };

  const removeSubMenuItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      subMenu: prev.subMenu.filter((_, i) => i !== index),
    }));
  };

  const handleSubMenuChange = (index, e) => {
    const { name, value } = e.target;
    const updatedSubMenu = [...formData.subMenu];
    updatedSubMenu[index][name] = value;

    const item = updatedSubMenu[index];
    if (item.discountType === "percentage" && (name === "price" || name === "discountPercentage" || name === "discountType")) {
      const p = parseFloat(item.price);
      const pct = parseFloat(item.discountPercentage);
      if (!isNaN(p) && !isNaN(pct)) {
        item.discountPrice = Math.round(p - (p * pct / 100)).toString();
      }
    }

    setFormData((prev) => ({ ...prev, subMenu: updatedSubMenu }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append("name", formData.name);
    data.append("description", formData.description);
    data.append("dietaryType", formData.dietaryType);
    data.append("brandType", formData.brandType);
    data.append("category", formData.category);
    data.append("specialTag", formData.specialTag);
    data.append("isAvailable", formData.isAvailable);

    if (formData.image) {
      data.append("image", formData.image);
    }

    if (pricingType === "single") {
      if (formData.price) {
        data.append("price", formData.price);
      }
      if (formData.discountPrice) {
        data.append("discountPrice", formData.discountPrice);
      }
      data.append("discountType", formData.discountType);
      if (formData.discountPercentage) {
        data.append("discountPercentage", formData.discountPercentage);
      }
    } else {
      // pricingType === 'portions'
      if (formData.portions.length > 0) {
        data.append("portions", JSON.stringify(formData.portions));
      }
    }

    if (formData.subMenu.length > 0) {
      data.append("subMenu", JSON.stringify(formData.subMenu));
    }

    const toastId = toast.loading(
      isEdit ? "Updating menu item..." : "Creating menu item..."
    );

    try {
      let response;
      if (isEdit) {
        response = await api.put(`/menu/${initialData._id}`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        response = await api.post("/menu", data, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data.success) {
        toast.success(
          isEdit ? "Menu item updated successfully!" : "Menu item created successfully!",
          { id: toastId }
        );
        if (onSuccess) onSuccess();
        if (!isEdit) {
          setFormData({
            name: "",
            description: "",
            price: "",
            discountPrice: "",
            discountType: "price",
            discountPercentage: "",
            image: null,
            dietaryType: "veg",
            brandType: "gravy_ganj",
            category: "",
            specialTag: "none",
            isAvailable: true,
            subMenu: [],
            portions: [],
          });
          setPricingType("single");
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      } else {
        toast.error(response.data.message || "Failed to create item.", {
          id: toastId,
        });
      }
    } catch (error) {
      console.error("Create Menu Error:", error);
      const errorMessage =
        error.response?.data?.message || "An error occurred.";
      toast.error(errorMessage, { id: toastId });
    }
  };

  const inputClass =
    "w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent";
  const buttonClass =
    "w-full py-3 rounded-lg text-white font-semibold disabled:opacity-50 transition";
  const subButtonClass =
    "px-4 py-1 rounded-lg text-sm font-semibold transition";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 max-w-2xl w-full">
        <h2 className="text-2xl font-bold mb-6 text-center text-green-700">
          {isEdit ? "Edit Menu Item" : "Add New Menu Item"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 gap-6">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Item Name"
              className={inputClass}
              required
            />
          </div>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Item Description"
            className={inputClass}
            required
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              name="dietaryType"
              value={formData.dietaryType}
              onChange={handleChange}
              className={inputClass}
              required
            >
              <option value="veg">Veg</option>
              <option value="non-veg">Non-Veg</option>
              <option value="contains-egg">Contains Egg</option>
            </select>
            <select
              name="brandType"
              value={formData.brandType}
              onChange={handleChange}
              className={inputClass}
              required
            >
              <option value="gravy_ganj">Gravy Ganj</option>
              <option value="kepsa_queue">Kepsa Queue</option>
              <option value="gutful_greens">Gutful Goodness - Greens</option>
            </select>
          </div>
          <div>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={inputClass}
              required
            >
              <option value="">Select Category</option>
              {categoryOptions[formData.brandType]?.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          {/* Special Tag & Availability */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Special Tag
              </label>
              <select
                name="specialTag"
                value={formData.specialTag}
                onChange={handleChange}
                className={inputClass}
              >
                <option value="none">None</option>
                <option value="chef_special">Chef's Special</option>
                <option value="most_ordered">Most Ordered</option>
                <option value="trending">Trending</option>
                <option value="new_arrival">New Arrival</option>
                <option value="bestseller">Bestseller</option>
                <option value="seasonal">Seasonal Special</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  name="isAvailable"
                  checked={formData.isAvailable}
                  onChange={handleChange}
                  className="w-5 h-5 text-green-600 rounded focus:ring-green-500 border-gray-300"
                />
                <span className="text-gray-700 font-medium">Available</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Item Image
            </label>
            <input
              type="file"
              accept="image/*"
              name="image"
              ref={fileInputRef}
              onChange={handleFileChange}
              className={`${inputClass} p-0 file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200`}
            />
          </div>

          {/* Pricing Type */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="pricingType"
                value="single"
                checked={pricingType === "single"}
                onChange={(e) => setPricingType(e.target.value)}
                className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300"
              />
              Single Price
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="pricingType"
                value="portions"
                checked={pricingType === "portions"}
                onChange={(e) => setPricingType(e.target.value)}
                className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300"
              />
              Portion-based Pricing
            </label>
          </div>

          {/* Single Price Fields */}
          {pricingType === "single" && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Base Price</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="Price"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Discount Method</label>
                  <div className="flex gap-4 h-10 items-center">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                      <input type="radio" name="discountType" value="price" checked={formData.discountType === "price"} onChange={handleChange} className="w-4 h-4 text-green-600 focus:ring-green-500" /> By Price
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                      <input type="radio" name="discountType" value="percentage" checked={formData.discountType === "percentage"} onChange={handleChange} className="w-4 h-4 text-green-600 focus:ring-green-500" /> By %
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formData.discountType === "percentage" && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Discount %</label>
                    <input
                      type="number"
                      name="discountPercentage"
                      value={formData.discountPercentage}
                      onChange={handleChange}
                      placeholder="e.g. 20"
                      className={inputClass}
                    />
                  </div>
                )}
                <div className={formData.discountType !== "percentage" ? "md:col-start-2" : ""}>
                  <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">Final Price (Discounted)</label>
                  <input
                    type="number"
                    name="discountPrice"
                    value={formData.discountPrice}
                    onChange={handleChange}
                    placeholder="Discount Price (Optional)"
                    className={`${inputClass} ${formData.discountType === "percentage" ? "bg-gray-100 text-gray-600 font-bold" : ""}`}
                    readOnly={formData.discountType === "percentage"}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Portions Fields */}
          {pricingType === "portions" && (
            <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
              <h3 className="font-semibold text-gray-800">Portions</h3>
              {formData.portions.map((portion, index) => (
                <div
                  key={index}
                  className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200 relative"
                >
                  <button
                    type="button"
                    onClick={() => removePortion(index)}
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700 font-bold"
                  >
                    &times;
                  </button>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase">Size</label>
                      <select
                        name="size"
                        value={portion.size}
                        onChange={(e) => handlePortionChange(index, e)}
                        className={inputClass}
                      >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase">Price</label>
                      <input
                        type="number"
                        name="price"
                        value={portion.price}
                        onChange={(e) => handlePortionChange(index, e)}
                        placeholder="Price"
                        className={inputClass}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 py-1 border-t border-gray-200 mt-2">
                    <span className="text-[10px] font-bold text-gray-500 uppercase">Discount:</span>
                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                      <input type="radio" name="discountType" value="price" checked={portion.discountType === "price"} onChange={(e) => handlePortionChange(index, e)} /> Price
                    </label>
                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                      <input type="radio" name="discountType" value="percentage" checked={portion.discountType === "percentage"} onChange={(e) => handlePortionChange(index, e)} /> %
                    </label>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {portion.discountType === "percentage" && (
                      <div>
                        <label className="text-[10px] font-bold text-gray-500 uppercase">Percent %</label>
                        <input
                          type="number"
                          name="discountPercentage"
                          value={portion.discountPercentage}
                          onChange={(e) => handlePortionChange(index, e)}
                          placeholder="%"
                          className={inputClass}
                        />
                      </div>
                    )}
                    <div className={portion.discountType !== "percentage" ? "sm:col-start-2" : ""}>
                      <label className="text-[10px] font-bold text-gray-500 uppercase">Discount Price</label>
                      <input
                        type="number"
                        name="discountPrice"
                        value={portion.discountPrice}
                        onChange={(e) => handlePortionChange(index, e)}
                        placeholder="Discount Price"
                        className={`${inputClass} ${portion.discountType === "percentage" ? "bg-gray-100" : ""}`}
                        readOnly={portion.discountType === "percentage"}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addPortion}
                className={`${subButtonClass} bg-blue-500 text-white hover:bg-blue-600`}
              >
                + Add Portion
              </button>
            </div>
          )}

          {/* Sub-Menu Section */}
          <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
            <h3 className="font-semibold text-gray-800">
              Sub-Menu Items (Optional)
            </h3>
            {formData.subMenu.map((item, index) => (
              <div
                key={index}
                className="p-4 border rounded-md space-y-3 relative bg-gray-50"
              >
                <button
                  type="button"
                  onClick={() => removeSubMenuItem(index)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700 font-bold"
                >
                  &times;
                </button>
                <input
                  type="text"
                  name="name"
                  value={item.name}
                  onChange={(e) => handleSubMenuChange(index, e)}
                  placeholder="Sub-item Name"
                  className={inputClass}
                />
                <textarea
                  name="description"
                  value={item.description}
                  onChange={(e) => handleSubMenuChange(index, e)}
                  placeholder="Sub-item Description"
                  className={inputClass}
                  rows="2"
                />
                <div className="flex flex-wrap items-center gap-4 py-1 border-gray-200">
                  <span className="text-[10px] font-bold text-gray-500 uppercase">Discount:</span>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="radio" name="discountType" value="price" checked={item.discountType === "price"} onChange={(e) => handleSubMenuChange(index, e)} /> Price
                  </label>
                  <label className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="radio" name="discountType" value="percentage" checked={item.discountType === "percentage"} onChange={(e) => handleSubMenuChange(index, e)} /> %
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase">Price</label>
                    <input
                      type="number"
                      name="price"
                      value={item.price}
                      onChange={(e) => handleSubMenuChange(index, e)}
                      placeholder="Price"
                      className={inputClass}
                    />
                  </div>
                  {item.discountType === "percentage" ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-gray-500 uppercase">%</label>
                        <input
                          type="number"
                          name="discountPercentage"
                          value={item.discountPercentage}
                          onChange={(e) => handleSubMenuChange(index, e)}
                          placeholder="%"
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-500 uppercase">Final</label>
                        <input
                          type="number"
                          name="discountPrice"
                          value={item.discountPrice}
                          onChange={(e) => handleSubMenuChange(index, e)}
                          placeholder="Discount"
                          className={`${inputClass} bg-gray-100`}
                          readOnly
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase">Discount Price</label>
                      <input
                        type="number"
                        name="discountPrice"
                        value={item.discountPrice}
                        onChange={(e) => handleSubMenuChange(index, e)}
                        placeholder="Discount Price"
                        className={inputClass}
                      />
                    </div>
                  )}
                </div>
                <select
                  name="dietaryType"
                  value={item.dietaryType}
                  onChange={(e) => handleSubMenuChange(index, e)}
                  className={inputClass}
                >
                  <option value="veg">Veg</option>
                  <option value="non-veg">Non-Veg</option>
                  <option value="contains-egg">Contains Egg</option>
                </select>
              </div>
            ))}
            <button
              type="button"
              onClick={addSubMenuItem}
              className={`${subButtonClass} bg-blue-500 text-white hover:bg-blue-600`}
            >
              + Add Sub-Menu Item
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className={buttonClass}
            style={{ backgroundColor: "#0B9444" }}
          >
            {isEdit ? "Update Menu Item" : "Add Menu Item"}
          </button>
          {isEdit && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full py-3 rounded-lg text-gray-700 font-semibold border border-gray-300 hover:bg-gray-50 transition mt-4"
            >
              Cancel
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default MenuForm;
